import type { ListingMeta } from './types'
import { SF_IN_ACRES } from '~/utils'

export type LotSizeUnit = 'acres' | 'sf'

export type LotSizeInput = {
  min?: number
  max?: number
  unit?: LotSizeUnit
}

export type LotSizeRange = {
  min?: number
  max?: number
}

export type NormalizedLotSize = {
  unit: LotSizeUnit
  value: LotSizeRange
  acres: LotSizeRange
  sf: LotSizeRange
}

export type PriceRange = {
  min?: number
  max?: number
}

export function getSuffix(unit: LotSizeUnit) {
  return unit === 'sf' ? 'SF' : 'ac'
}

function isPositive(value: number | undefined): value is number {
  return typeof value === 'number' && value > 0 && Number.isFinite(value)
}

function roundAcres(value: number) {
  return Math.round(value * 100_000_000) / 100_000_000
}

function roundSf(value: number) {
  return Math.round(value * 100) / 100
}

function normalizeRange(min?: number, max?: number): LotSizeRange {
  return {
    min: isPositive(min) ? min : undefined,
    max: isPositive(max) ? max : isPositive(min) ? min : undefined,
  }
}

export function normalizeLotSize(input: LotSizeInput): NormalizedLotSize {
  const unit = input.unit ?? 'acres'
  const value = normalizeRange(input.min, input.max)

  if (unit === 'sf') {
    const sf = value
    return {
      unit,
      value,
      sf,
      acres: {
        min:
          sf.min !== undefined ? roundAcres(sf.min / SF_IN_ACRES) : undefined,
        max:
          sf.max !== undefined ? roundAcres(sf.max / SF_IN_ACRES) : undefined,
      },
    }
  }

  const acres = value
  return {
    unit,
    value,
    acres,
    sf: {
      min:
        acres.min !== undefined ? roundSf(acres.min * SF_IN_ACRES) : undefined,
      max:
        acres.max !== undefined ? roundSf(acres.max * SF_IN_ACRES) : undefined,
    },
  }
}

export function getLotSizeFromMeta(meta: ListingMeta): NormalizedLotSize {
  if (isPositive(meta.lotSizeMin)) {
    return normalizeLotSize({
      min: meta.lotSizeMin,
      max: meta.lotSizeMax,
      unit: meta.lotSizeUnit,
    })
  }

  return normalizeLotSize({
    min: meta.acres,
    max: meta.acresMax,
    unit: 'acres',
  })
}

function getRangeValue(range: PriceRange, key: 'min' | 'max') {
  return range[key] ?? range.min ?? range.max
}

function multiplyRanges(price: PriceRange, size: LotSizeRange): PriceRange {
  const minPrice = getRangeValue(price, 'min')
  const maxPrice = getRangeValue(price, 'max')
  const minSize = getRangeValue(size, 'min')
  const maxSize = getRangeValue(size, 'max')

  return {
    min:
      isPositive(minPrice) && isPositive(minSize)
        ? Math.round(minPrice * minSize)
        : undefined,
    max:
      isPositive(maxPrice) && isPositive(maxSize)
        ? Math.round(maxPrice * maxSize)
        : undefined,
  }
}

function divideBySizeEnvelope(
  price: PriceRange,
  size: LotSizeRange
): PriceRange {
  const minPrice = getRangeValue(price, 'min')
  const maxPrice = getRangeValue(price, 'max')
  const minSize = getRangeValue(size, 'min')
  const maxSize = getRangeValue(size, 'max')

  return {
    min:
      isPositive(minPrice) && isPositive(maxSize)
        ? minPrice / maxSize
        : undefined,
    max:
      isPositive(maxPrice) && isPositive(minSize)
        ? maxPrice / minSize
        : undefined,
  }
}

export function getTotalPriceFromSfPrice(
  pricePerSf: PriceRange,
  lotSizeSf: LotSizeRange
) {
  return multiplyRanges(pricePerSf, lotSizeSf)
}

export function getTotalPriceFromAcrePrice(
  pricePerAcre: PriceRange,
  lotSizeAcres: LotSizeRange
) {
  return multiplyRanges(pricePerAcre, lotSizeAcres)
}

export function getPricePerSfFromTotalPrice(
  totalPrice: PriceRange,
  lotSizeSf: LotSizeRange
) {
  return divideBySizeEnvelope(totalPrice, lotSizeSf)
}

export function getPricePerAcreFromTotalPrice(
  totalPrice: PriceRange,
  lotSizeAcres: LotSizeRange
) {
  return divideBySizeEnvelope(totalPrice, lotSizeAcres)
}
